/**
 * Professional Airport Database Service
 * Uses real IATA codes, accurate distances, and dynamic pricing algorithms
 */

export interface Airport {
  iata: string;
  icao: string;
  name: string;
  city: string;
  country: string;
  continent: string;
  latitude: number;
  longitude: number;
  timezone: string;
  hub_airlines: string[];
}

export interface RouteDistance {
  origin: Airport;
  destination: Airport;
  distance_km: number;
  distance_miles: number;
  flight_time_minutes: number;
  popular_airlines: string[];
}

export interface DynamicPricing {
  base_points: {
    economy: number;
    premium_economy: number;
    business: number;
    first: number;
  };
  base_cash_usd: {
    economy: number;
    premium_economy: number;
    business: number;
    first: number;
  };
  demand_multiplier: number; // 0.8 to 2.0 based on season/demand
  availability_factor: number; // 0.9 to 1.5 based on award availability
  last_updated: string;
}

class AirportDatabaseService {
  // Real Indian airports with accurate data
  private static readonly INDIAN_AIRPORTS: Record<string, Airport> = {
    'BOM': {
      iata: 'BOM',
      icao: 'VABB',
      name: 'Chhatrapati Shivaji Maharaj International Airport',
      city: 'Mumbai',
      country: 'India',
      continent: 'Asia',
      latitude: 19.0896,
      longitude: 72.8656,
      timezone: 'Asia/Kolkata',
      hub_airlines: ['Air India', 'Vistara', 'IndiGo', 'SpiceJet']
    },
    'DEL': {
      iata: 'DEL',
      icao: 'VIDP',
      name: 'Indira Gandhi International Airport',
      city: 'Delhi',
      country: 'India',
      continent: 'Asia',
      latitude: 28.5562,
      longitude: 77.1000,
      timezone: 'Asia/Kolkata',
      hub_airlines: ['Air India', 'Vistara', 'IndiGo', 'SpiceJet']
    },
    'BLR': {
      iata: 'BLR',
      icao: 'VOBL',
      name: 'Kempegowda International Airport',
      city: 'Bangalore',
      country: 'India',
      continent: 'Asia',
      latitude: 13.1979,
      longitude: 77.7063,
      timezone: 'Asia/Kolkata',
      hub_airlines: ['Air India', 'IndiGo', 'SpiceJet']
    },
    'MAA': {
      iata: 'MAA',
      icao: 'VOMM',
      name: 'Chennai International Airport',
      city: 'Chennai',
      country: 'India',
      continent: 'Asia',
      latitude: 13.0827,
      longitude: 80.2707,
      timezone: 'Asia/Kolkata',
      hub_airlines: ['Air India', 'IndiGo', 'SpiceJet']
    },
    'HYD': {
      iata: 'HYD',
      icao: 'VOHS',
      name: 'Rajiv Gandhi International Airport',
      city: 'Hyderabad',
      country: 'India',
      continent: 'Asia',
      latitude: 17.2313,
      longitude: 78.4298,
      timezone: 'Asia/Kolkata',
      hub_airlines: ['Air India', 'IndiGo', 'SpiceJet']
    },
    'CCU': {
      iata: 'CCU',
      icao: 'VECC',
      name: 'Netaji Subhas Chandra Bose International Airport',
      city: 'Kolkata',
      country: 'India',
      continent: 'Asia',
      latitude: 22.6547,
      longitude: 88.4467,
      timezone: 'Asia/Kolkata',
      hub_airlines: ['Air India', 'IndiGo', 'SpiceJet']
    }
  };

  // Popular international destinations from India
  private static readonly INTERNATIONAL_AIRPORTS: Record<string, Airport> = {
    'SIN': {
      iata: 'SIN',
      icao: 'WSSS',
      name: 'Singapore Changi Airport',
      city: 'Singapore',
      country: 'Singapore',
      continent: 'Asia',
      latitude: 1.3644,
      longitude: 103.9915,
      timezone: 'Asia/Singapore',
      hub_airlines: ['Singapore Airlines', 'Scoot', 'Jetstar Asia']
    },
    'DXB': {
      iata: 'DXB',
      icao: 'OMDB',
      name: 'Dubai International Airport',
      city: 'Dubai',
      country: 'UAE',
      continent: 'Asia',
      latitude: 25.2528,
      longitude: 55.3644,
      timezone: 'Asia/Dubai',
      hub_airlines: ['Emirates', 'flydubai']
    },
    'LHR': {
      iata: 'LHR',
      icao: 'EGLL',
      name: 'London Heathrow Airport',
      city: 'London',
      country: 'United Kingdom',
      continent: 'Europe',
      latitude: 51.4700,
      longitude: -0.4543,
      timezone: 'Europe/London',
      hub_airlines: ['British Airways', 'Virgin Atlantic']
    },
    'FRA': {
      iata: 'FRA',
      icao: 'EDDF',
      name: 'Frankfurt Airport',
      city: 'Frankfurt',
      country: 'Germany',
      continent: 'Europe',
      latitude: 50.0379,
      longitude: 8.5622,
      timezone: 'Europe/Berlin',
      hub_airlines: ['Lufthansa', 'Condor']
    },
    'CDG': {
      iata: 'CDG',
      icao: 'LFPG',
      name: 'Charles de Gaulle Airport',
      city: 'Paris',
      country: 'France',
      continent: 'Europe',
      latitude: 49.0097,
      longitude: 2.5479,
      timezone: 'Europe/Paris',
      hub_airlines: ['Air France', 'Air France-KLM']
    },
    'NRT': {
      iata: 'NRT',
      icao: 'RJAA',
      name: 'Narita International Airport',
      city: 'Tokyo',
      country: 'Japan',
      continent: 'Asia',
      latitude: 35.7653,
      longitude: 140.3864,
      timezone: 'Asia/Tokyo',
      hub_airlines: ['Japan Airlines', 'ANA']
    },
    'JFK': {
      iata: 'JFK',
      icao: 'KJFK',
      name: 'John F. Kennedy International Airport',
      city: 'New York',
      country: 'United States',
      continent: 'North America',
      latitude: 40.6413,
      longitude: -73.7781,
      timezone: 'America/New_York',
      hub_airlines: ['Delta', 'JetBlue', 'American Airlines']
    },
    'LAX': {
      iata: 'LAX',
      icao: 'KLAX',
      name: 'Los Angeles International Airport',
      city: 'Los Angeles',
      country: 'United States',
      continent: 'North America',
      latitude: 33.9425,
      longitude: -118.4081,
      timezone: 'America/Los_Angeles',
      hub_airlines: ['Delta', 'American Airlines', 'United']
    }
  };

  /**
   * Calculate great circle distance between two airports
   */
  static calculateDistance(origin: Airport, destination: Airport): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(destination.latitude - origin.latitude);
    const dLon = this.toRadians(destination.longitude - origin.longitude);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(origin.latitude)) * Math.cos(this.toRadians(destination.latitude)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
  }

  /**
   * Estimate flight time based on distance
   */
  static estimateFlightTime(distanceKm: number): number {
    // Average commercial aircraft speed: 900 km/h including taxi, takeoff, landing
    const averageSpeed = 850;
    const flightTime = (distanceKm / averageSpeed) * 60; // Convert to minutes
    
    // Add buffer time for taxi, takeoff, landing
    const bufferTime = distanceKm < 3000 ? 30 : 60; // minutes
    
    return Math.round(flightTime + bufferTime);
  }

  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Get all available airports
   */
  static getAllAirports(): Record<string, Airport> {
    return { ...this.INDIAN_AIRPORTS, ...this.INTERNATIONAL_AIRPORTS };
  }

  /**
   * Get popular routes from Indian cities
   */
  static getPopularRoutes(): RouteDistance[] {
    const routes: RouteDistance[] = [];
    const allAirports = this.getAllAirports();
    
    // Define popular route pairs
    const popularRoutePairs = [
      ['BOM', 'SIN'], ['BOM', 'DXB'], ['BOM', 'LHR'],
      ['DEL', 'DXB'], ['DEL', 'LHR'], ['DEL', 'FRA'],
      ['BLR', 'SIN'], ['BLR', 'LHR'], ['BLR', 'FRA'],
      ['MAA', 'SIN'], ['MAA', 'DXB'], ['MAA', 'FRA'],
      ['HYD', 'DXB'], ['HYD', 'LHR'], ['HYD', 'CDG'],
      ['CCU', 'SIN'], ['CCU', 'NRT'], ['CCU', 'DXB'],
      ['BOM', 'JFK'], ['DEL', 'LAX'], ['BLR', 'JFK']
    ];

    popularRoutePairs.forEach(([originCode, destCode]) => {
      const origin = allAirports[originCode];
      const destination = allAirports[destCode];
      
      if (origin && destination) {
        const distance = this.calculateDistance(origin, destination);
        const flightTime = this.estimateFlightTime(distance);
        
        routes.push({
          origin,
          destination,
          distance_km: distance,
          distance_miles: Math.round(distance * 0.621371),
          flight_time_minutes: flightTime,
          popular_airlines: this.getPopularAirlinesForRoute(origin, destination)
        });
      }
    });

    return routes.sort((a, b) => a.distance_km - b.distance_km);
  }

  /**
   * Get airlines that commonly serve a route
   */
  private static getPopularAirlinesForRoute(origin: Airport, destination: Airport): string[] {
    const airlines = new Set<string>();
    
    // Add hub airlines from both airports
    origin.hub_airlines.forEach(airline => airlines.add(airline));
    destination.hub_airlines.forEach(airline => airlines.add(airline));
    
    // Add major international carriers for long-haul routes
    const distance = this.calculateDistance(origin, destination);
    if (distance > 5000) {
      ['Emirates', 'Qatar Airways', 'Singapore Airlines', 'Lufthansa'].forEach(airline => airlines.add(airline));
    }
    
    return Array.from(airlines).slice(0, 4); // Limit to 4 airlines
  }

  /**
   * Generate dynamic pricing for a route
   */
  static generateDynamicPricing(route: RouteDistance, seasonality?: 'low' | 'medium' | 'high'): DynamicPricing {
    const distance = route.distance_km;
    
    // Base pricing algorithm based on distance
    const basePointsMultiplier = Math.max(0.8, Math.min(2.0, distance / 4000));
    const baseCashMultiplier = Math.max(0.9, Math.min(1.8, distance / 5000));
    
    // Seasonality factors
    const seasonMultipliers = {
      low: 0.8,
      medium: 1.0,
      high: 1.3
    };
    
    const seasonFactor = seasonMultipliers[seasonality || 'medium'];
    
    // Generate demand and availability factors
    const demandMultiplier = 0.9 + (Math.random() * 0.6); // 0.9 to 1.5
    const availabilityFactor = 0.8 + (Math.random() * 0.4); // 0.8 to 1.2
    
    return {
      base_points: {
        economy: Math.round(15000 * basePointsMultiplier * seasonFactor),
        premium_economy: Math.round(22000 * basePointsMultiplier * seasonFactor),
        business: Math.round(45000 * basePointsMultiplier * seasonFactor),
        first: Math.round(75000 * basePointsMultiplier * seasonFactor)
      },
      base_cash_usd: {
        economy: Math.round(400 * baseCashMultiplier * seasonFactor),
        premium_economy: Math.round(800 * baseCashMultiplier * seasonFactor),
        business: Math.round(1800 * baseCashMultiplier * seasonFactor),
        first: Math.round(4500 * baseCashMultiplier * seasonFactor)
      },
      demand_multiplier: Math.round(demandMultiplier * 100) / 100,
      availability_factor: Math.round(availabilityFactor * 100) / 100,
      last_updated: new Date().toISOString()
    };
  }

  /**
   * Search routes by origin city or airport code
   */
  static searchRoutesByOrigin(query: string): RouteDistance[] {
    const allRoutes = this.getPopularRoutes();
    const queryLower = query.toLowerCase();
    
    return allRoutes.filter(route => 
      route.origin.iata.toLowerCase().includes(queryLower) ||
      route.origin.city.toLowerCase().includes(queryLower) ||
      route.origin.name.toLowerCase().includes(queryLower)
    );
  }

  /**
   * Search routes by destination
   */
  static searchRoutesByDestination(query: string): RouteDistance[] {
    const allRoutes = this.getPopularRoutes();
    const queryLower = query.toLowerCase();
    
    return allRoutes.filter(route => 
      route.destination.iata.toLowerCase().includes(queryLower) ||
      route.destination.city.toLowerCase().includes(queryLower) ||
      route.destination.name.toLowerCase().includes(queryLower)
    );
  }

  /**
   * Get route between specific airports
   */
  static getSpecificRoute(originCode: string, destCode: string): RouteDistance | null {
    const allAirports = this.getAllAirports();
    const origin = allAirports[originCode.toUpperCase()];
    const destination = allAirports[destCode.toUpperCase()];
    
    if (!origin || !destination) return null;
    
    const distance = this.calculateDistance(origin, destination);
    const flightTime = this.estimateFlightTime(distance);
    
    return {
      origin,
      destination,
      distance_km: distance,
      distance_miles: Math.round(distance * 0.621371),
      flight_time_minutes: flightTime,
      popular_airlines: this.getPopularAirlinesForRoute(origin, destination)
    };
  }
}

export default AirportDatabaseService;